import { z } from 'zod';

/**
 * pm2 reports a handful of lifecycle states. `launching` is transient and the
 * app animates it, so it must round-trip rather than being collapsed into
 * online/stopped.
 */
export const ProcessStatusSchema = z.enum([
  'online',
  'stopping',
  'stopped',
  'launching',
  'errored',
  'one-launch-status',
]);
export type ProcessStatus = z.infer<typeof ProcessStatusSchema>;

export const ExecModeSchema = z.enum(['fork_mode', 'cluster_mode']);
export type ExecMode = z.infer<typeof ExecModeSchema>;

/**
 * A single process as shown in the dashboard list. Memory is bytes and cpu is a
 * percentage, both straight from pm2's `monit` block.
 */
export const ProcessSummarySchema = z.object({
  id: z.number(),
  name: z.string(),
  namespace: z.string(),
  status: ProcessStatusSchema,
  pid: z.number().nullable(),
  cpu: z.number(),
  memory: z.number(),
  /** Epoch ms the process last came up; null when it has never started. */
  uptimeSince: z.number().nullable(),
  restarts: z.number(),
  unstableRestarts: z.number(),
  instances: z.number(),
  execMode: ExecModeSchema,
  version: z.string().nullable(),
  script: z.string(),
  cwd: z.string(),
  autorestart: z.boolean(),
  /** Bytes; pm2 restarts the process above this. null when unset. */
  maxMemoryRestart: z.number().nullable(),
});
export type ProcessSummary = z.infer<typeof ProcessSummarySchema>;

/**
 * pm2's git metadata, present when the process was started from a repo. This
 * backs the detail screen's versioning card.
 */
export const VersioningSchema = z.object({
  type: z.string().nullable(),
  url: z.string().nullable(),
  branch: z.string().nullable(),
  revision: z.string().nullable(),
  comment: z.string().nullable(),
  unstaged: z.boolean().nullable(),
});
export type Versioning = z.infer<typeof VersioningSchema>;

/**
 * One entry from pm2's `axm_monitor` — richer runtime telemetry than the plain
 * cpu/memory pair (heap usage, event-loop latency, HTTP throughput).
 * pm2 emits `value` as either a string or a number depending on the metric, so
 * it is normalised to a number here and the raw display string kept alongside.
 */
export const RuntimeMetricSchema = z.object({
  name: z.string(),
  value: z.number().nullable(),
  displayValue: z.string(),
  type: z.string().nullable(),
  unit: z.string().nullable(),
  historic: z.boolean(),
});
export type RuntimeMetric = z.infer<typeof RuntimeMetricSchema>;

export const ProcessDetailSchema = ProcessSummarySchema.extend({
  execPath: z.string(),
  interpreter: z.string().nullable(),
  nodeVersion: z.string().nullable(),
  createdAt: z.number().nullable(),
  exitCode: z.number().nullable(),
  username: z.string().nullable(),
  watch: z.boolean(),
  outLogPath: z.string().nullable(),
  errLogPath: z.string().nullable(),
  versioning: VersioningSchema.nullable(),
  metrics: z.array(RuntimeMetricSchema),
  /** Redacted before leaving the bridge — see `redactEnv`. */
  env: z.record(z.string()),
});
export type ProcessDetail = z.infer<typeof ProcessDetailSchema>;
